/**
 * Shared helpers for reading a tileset's per-tile metadata (engine 45.1 parity
 * + the animation-preview extension, _docs/tilemap/07 roadmap) from a tile id.
 * Used by the collision-preview overlay, the animation-preview overlay, and
 * (eventually) any export-side logic that needs the same classification the
 * engine will apply.
 */

import type { Tileset, TileAnimation } from './types';
import type { TileID } from './constants';
import { EMPTY_TILE } from './constants';

/** True if `id` is non-empty and its atlas index (`id - 1`) is marked solid. */
export function isTileSolid(tileset: Tileset, id: TileID): boolean {
    if (id === EMPTY_TILE) return false;
    const atlasIndex = id - 1;
    return tileset.tile_meta?.[String(atlasIndex)]?.solid === true;
}

/** The tile's animation definition, or undefined if `id` isn't animated. */
export function tileAnimationFor(tileset: Tileset, id: TileID): TileAnimation | undefined {
    if (id === EMPTY_TILE) return undefined;
    const atlasIndex = id - 1;
    return tileset.tile_meta?.[String(atlasIndex)]?.animation ?? undefined;
}

/**
 * Which atlas index should be shown right now, given how long the animation
 * has been running. Forward-looping only (no ping-pong/reverse — a
 * deliberate v1 cut, _docs/tilemap/07 roadmap).
 */
export function currentFrameAtlasIndex(animation: TileAnimation, elapsedMs: number): number {
    const { frames, frameDurationMs } = animation;
    if (frames.length === 0) return 0;
    if (frameDurationMs <= 0) return frames[0];
    const frameIndex = Math.floor(elapsedMs / frameDurationMs) % frames.length;
    return frames[frameIndex];
}
